import { memo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartFrame } from '@/components/charts/ChartFrame';
import { formatCompact } from '@/lib/format';
import { TIER_COLORS } from '@/lib/tierColors';

type CashFlowChartProps = {
  data: { month: number; cashFlow: number }[];
  globalBreakEvenMonth: number | null;
};

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  color: 'var(--card-foreground)',
  fontSize: 12,
};

export const CashFlowChart = memo(function CashFlowChart({
  data,
  globalBreakEvenMonth,
}: CashFlowChartProps) {
  return (
    <ChartFrame>
      <LineChart data={data} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis
          dataKey="month"
          tickFormatter={(v: number) => `M${v}`}
          tick={{ fill: 'currentColor', fontSize: 11 }}
        />
        <YAxis
          tickFormatter={formatCompact}
          tick={{ fill: 'currentColor', fontSize: 11 }}
          width={56}
        />
        <Tooltip
          formatter={(value) =>
            typeof value === 'number' ? [formatCompact(value), 'Cash flow cumulé'] : value
          }
          contentStyle={TOOLTIP_STYLE}
          labelFormatter={(label) => `Mois ${String(label)}`}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine y={0} stroke="#888" strokeDasharray="4 2" />
        {globalBreakEvenMonth !== null && (
          <ReferenceLine
            x={globalBreakEvenMonth}
            stroke={TIER_COLORS[2]}
            strokeDasharray="4 2"
            label={{ value: 'ROI', position: 'insideTopRight', fontSize: 11, fill: TIER_COLORS[2] }}
          />
        )}
        <Line
          type="monotone"
          dataKey="cashFlow"
          name="Cash flow cumulé"
          stroke={TIER_COLORS[0]}
          dot={false}
          strokeWidth={2}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartFrame>
  );
});
